import { useCallback, useMemo } from 'react';

import { Box, Typography } from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  OomKillsScatterChart,
  type OomKillWorkloadHrefArgs,
} from '@cast/split-me/features/cluster/dashboard/WorkloadEventMetricsSection';
import { dashesOrNum, toNumberWithSeparators } from '@cast/utils';

import { ChartWithTopWorkloadsCard } from '../../../_components/ui/ChartWithTopWorkloadsCard';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { WORKLOAD_CATEGORIES, WORKLOAD_VIEWS } from '../../../_constants';
import { buildInventoryWorkloadViewPath } from '../../../navigation';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { useInventoryOomKillsData } from './_hooks/useInventoryOomKillsData';

const formatCount = (value: number) =>
  dashesOrNum({
    value,
    mapNumber: (num) => toNumberWithSeparators(num) || '0',
  });

export const OomKillsCard = () => {
  const {
    chartData,
    topItems,
    isLoading,
    isFetching,
    error,
    refetch,
    timeZone,
    rangeSelection,
  } = useInventoryOomKillsData();
  const { getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<string>();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const listItems = useMemo(
    () =>
      topItems.map((item) => ({
        id: item.workloadId || item.id,
        name: item.name,
        color: item.color,
        value: formatCount(item.value),
      })),
    [topItems]
  );

  const getWorkloadHref = useCallback(
    ({ clusterId, workloadId }: OomKillWorkloadHrefArgs) =>
      buildInventoryWorkloadViewPath({
        clusterId,
        workloadId,
        view: WORKLOAD_VIEWS.DASHBOARD,
        category: WORKLOAD_CATEGORIES.AUTOSCALING,
      }),
    []
  );

  const cardContent = (
    <ChartWithTopWorkloadsCard
      testId="oom-kills-card"
      leftHeader={<Typography variant="A1">OOMKills</Typography>}
      rightHeader={<Typography variant="A1">Managed vs Unmanaged</Typography>}
      chart={
        <Box flex={1} minHeight={200} width="100%">
          <OomKillsScatterChart
            data={chartData}
            timeZone={timeZone}
            isLoading={isCardLoading}
            hasNoData={!isCardLoading && !chartData.length}
            testId="oom-kills-chart"
            rangeSelection={rangeSelection}
            getWorkloadHref={getWorkloadHref}
            hideManaged={isMetricDisabled('managed')}
            hideUnmanaged={isMetricDisabled('unmanaged')}
            sx={{ height: '100%' }}
          />
        </Box>
      }
      items={listItems}
      isListLoading={isCardLoading && !topItems.length}
      onItemClick={toggleMetric}
      isItemDisabled={isMetricDisabled}
      getItemHref={(item) => {
        const topItem = topItems.find(
          (candidate) => (candidate.workloadId || candidate.id) === item.id
        );
        if (!topItem?.workloadId) {
          return undefined;
        }
        return getWorkloadDashboardPath(
          topItem.workloadId,
          WORKLOAD_CATEGORIES.AUTOSCALING
        );
      }}
    />
  );

  if (hasError && !isCardLoading) {
    return (
      <WidgetLayout testId="oom-kills-card" padding={false}>
        <FailedToLoad title="Failed to load OOM kills" refresh={refetch} />
      </WidgetLayout>
    );
  }

  return cardContent;
};
